// The batch-assignment pages' own rules: form checks, dates as words, the
// grading queue and the marks export. Pure.
//
// Whether a student may hand in is NOT decided here — the server answers it as
// `submissionState` (vc_be/utils/generalAssignmentRules.submissionWindow) and
// the student page renders that. The form checks below are the server's create
// and edit checks, in the same words, so a form the page lets through is never
// refused for a reason the page could have said.

// The vocabulary module itself, not the barrel: this file is pure, and the
// barrel would bring every component (and the router) in with it.
import { resolveStatus } from "../../ui/statusVocabulary";

export const LIMITS = {
  title: 200,
  description: 10000,
  textResponse: 20000,
  feedback: 5000,
  maxMarks: 1000,
};

/** Where the staff pages live for this user. */
export const assignmentsBase = (usertype) =>
  usertype === "instructor" ? "/i/batch-assignments" : "/batch-assignments";

const isBlank = (v) => v === undefined || v === null || String(v).trim() === "";

/** "7.5" → 7.5 · "" → null · "abc" → NaN. */
export function parseNumber(value) {
  if (isBlank(value)) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
}

const hasAtMostTwoDecimals = (n) => Math.abs(Math.round(n * 100) - n * 100) < 1e-6;

export function maxMarksProblem(n) {
  if (n === null) return "Enter the marks it is out of.";
  if (Number.isNaN(n)) return "Out of must be a number.";
  if (n <= 0) return "Out of must be more than 0.";
  if (n > LIMITS.maxMarks) return `Out of can be at most ${LIMITS.maxMarks}.`;
  if (!hasAtMostTwoDecimals(n)) return "Use at most two decimal places.";
  return null;
}

export function marksProblem(n, maxMarks) {
  if (n === null) return "Enter the marks.";
  if (Number.isNaN(n)) return "Marks must be a number.";
  if (n < 0) return "Marks can't be below 0.";
  if (Number.isFinite(Number(maxMarks)) && n > Number(maxMarks)) return `Marks can't be more than ${maxMarks}.`;
  if (!hasAtMostTwoDecimals(n)) return "Use at most two decimal places.";
  return null;
}

/**
 * The create / edit form's checks.
 * @param {{ batchId, title, description, dueDate: "YYYY-MM-DDTHH:mm", maxMarks }} values
 * @param {{ creating: boolean, original?: object, now?: Date }} opts
 * @returns {Record<string,string>} field → message; empty when valid
 */
export function validateAssignmentForm(values, { creating, original = null, now = new Date() }) {
  const errors = {};
  if (creating && !values.batchId) errors.batchId = "Choose the batch whose students do it.";

  const title = String(values.title || "").trim();
  if (!title) errors.title = "Give the assignment a title.";
  else if (title.length > LIMITS.title) errors.title = `Keep the title under ${LIMITS.title} characters.`;

  if (String(values.description || "").trim().length > LIMITS.description) {
    errors.description = `Keep the instructions under ${LIMITS.description.toLocaleString("en-IN")} characters.`;
  }

  if (isBlank(values.dueDate)) errors.dueDate = "Set a due date.";
  else {
    const due = new Date(values.dueDate);
    if (Number.isNaN(due.getTime())) errors.dueDate = "That due date is not a valid date.";
    else if (creating && due <= now) errors.dueDate = "Pick a due date in the future.";
  }

  const marks = parseNumber(values.maxMarks);
  if (!original || Number(original.maxMarks) !== marks) {
    const problem = maxMarksProblem(marks);
    if (problem) errors.maxMarks = problem;
  }
  return errors;
}

/* ── Dates as words ────────────────────────────────────────────────────────── */

/**
 * "Fri, 24 Oct, 11:59 pm" — or "Fri, 24 Oct" for a date-only due date. The year
 * shows only when it is not this one, or when `withYear` asks for it.
 */
export function formatDue(value, { dateOnly = false, withYear = false, now = new Date() } = {}) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const date = d.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(withYear || d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
  });
  if (dateOnly) return date;
  const time = d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" }).toLowerCase();
  return `${date}, ${time}`;
}

const unit = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** "in 3 days" · "in 2 hours" · "5 minutes ago". */
export function relativeTime(value, now = new Date()) {
  if (!value) return "";
  const ms = new Date(value).getTime() - now.getTime();
  const abs = Math.abs(ms);
  const HOUR = 3600000;
  const DAY = 86400000;
  let span;
  if (abs < HOUR) span = unit(Math.max(1, Math.round(abs / 60000)), "minute");
  else if (abs < DAY) span = unit(Math.round(abs / HOUR), "hour");
  else span = unit(Math.round(abs / DAY), "day");
  return ms >= 0 ? `in ${span}` : `${span} ago`;
}

/** Due within a day and not yet past — worth a warning tone. */
export const isDueSoon = (deadline, now = new Date()) => {
  if (!deadline) return false;
  const ms = new Date(deadline).getTime() - now.getTime();
  return ms > 0 && ms < 86400000;
};

/** A quick-pick due date at 11:59 pm, `days` from today, as a datetime-local value. */
export function dueInDays(days, now = new Date()) {
  const d = new Date(now);
  d.setDate(d.getDate() + days);
  d.setHours(23, 59, 0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T23:59`;
}

/* ── The grading queue ─────────────────────────────────────────────────────── */

/** The submission views, in the order the grader works through them. */
export const REVIEW_VIEWS = ["submitted", "resubmit_requested", "graded", "not_submitted", "all"];

/**
 * One row per student: their submission, or a "not submitted" row for each
 * student of the batch who has handed nothing in.
 */
export function reviewRows(submissions = [], notSubmitted = []) {
  const handedIn = submissions.map((s) => ({
    key: s._id,
    submissionId: s._id,
    student: s.studentId || {},
    status: s.status,
    submittedAt: s.submittedAt,
    late: !!s.late,
    attempt: s.attempt || 1,
    marks: s.marks,
    feedback: s.feedback || "",
    fileCount: (s.attachments || []).length,
    hasText: !!String(s.textResponse || "").trim(),
  }));
  const missing = notSubmitted.map((st) => ({
    key: `missing-${st._id}`,
    submissionId: null,
    student: st,
    status: "not_submitted",
    submittedAt: null,
    late: false,
    attempt: 0,
    marks: null,
    feedback: "",
    fileCount: 0,
    hasText: false,
  }));
  return [...handedIn, ...missing];
}

export function viewCounts(rows) {
  const counts = { submitted: 0, resubmit_requested: 0, graded: 0, not_submitted: 0, all: rows.length };
  for (const r of rows) if (counts[r.status] !== undefined) counts[r.status] += 1;
  return counts;
}

export const rowsInView = (rows, view) => (view === "all" ? rows : rows.filter((r) => r.status === view));

const GROUP = { submitted: 0, resubmit_requested: 1, graded: 2, not_submitted: 3 };

/**
 * Rows in the order a grader works through them: still to grade (oldest first —
 * they have waited longest), then those sent back, then graded, then the
 * students who have handed nothing in, by name. The table's default order and
 * "Save & next" are both this.
 */
export function sortForGrading(rows) {
  return [...rows].sort((a, b) => (GROUP[a.status] ?? 4) - (GROUP[b.status] ?? 4)
    || (a.submittedAt && b.submittedAt ? new Date(a.submittedAt) - new Date(b.submittedAt) : 0)
    || String(a.student?.name || "").localeCompare(String(b.student?.name || "")));
}

/** Submission ids in grading order. */
export const gradingOrder = (rows) => sortForGrading(rows).filter((r) => r.submissionId).map((r) => r.submissionId);

/** The next submission still to grade after `currentId`, wrapping round; null when none is left. */
export function nextToGrade(rows, currentId) {
  const waiting = gradingOrder(rows.filter((r) => r.status === "submitted"));
  if (!waiting.length) return null;
  const order = gradingOrder(rows);
  const from = order.indexOf(currentId);
  const after = order.slice(from + 1).concat(order.slice(0, Math.max(from, 0)));
  return after.find((id) => id !== currentId && waiting.includes(id)) || null;
}

export function averageMarks(rows) {
  const marks = rows.filter((r) => r.status === "graded" && Number.isFinite(r.marks)).map((r) => r.marks);
  if (!marks.length) return null;
  return Math.round((marks.reduce((a, b) => a + b, 0) / marks.length) * 100) / 100;
}

export const percentOf = (marks, maxMarks) =>
  Number.isFinite(marks) && Number(maxMarks) > 0 ? Math.round((marks / Number(maxMarks)) * 100) : null;

/* ── Export ────────────────────────────────────────────────────────────────── */

/**
 * One cell. A value that starts with = + - @ is prefixed with an apostrophe:
 * a spreadsheet would otherwise run it as a formula — and student names and
 * feedback are typed by people.
 */
export function csvCell(value) {
  let s = value === null || value === undefined ? "" : String(value);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** The marks register as CSV, with a BOM so Excel reads names in any script. */
export function marksCsv(rows, { maxMarks }) {
  const header = ["Student", "Email", "Status", "Submitted", "Late", "Attempt", "Marks", "Out of", "Feedback"];
  const lines = rows.map((r) => [
    r.student?.name || "",
    r.student?.email || "",
    resolveStatus(r.status, "assignmentReview").label,
    r.submittedAt ? formatDue(r.submittedAt, { withYear: true }) : "",
    r.late ? "Yes" : "",
    r.attempt || "",
    Number.isFinite(r.marks) ? r.marks : "",
    maxMarks ?? "",
    r.feedback || "",
  ].map(csvCell).join(","));
  return `﻿${[header.join(","), ...lines].join("\r\n")}`;
}
